'use client'

import { Slot } from '@radix-ui/react-slot'
import { Button } from 'components/Button'
import * as React from 'react'
import { cn } from 'utils/cn'

const SIDEBAR_COOKIE_NAME = 'sidebar_state'
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = '13.375rem'
const SIDEBAR_WIDTH_ICON = '4.5rem'

type SidebarContext = {
	state: 'expanded' | 'collapsed'
	open: boolean
	setOpen: (open: boolean) => void
	toggleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContext | null>(null)

function useSidebar() {
	const context = React.useContext(SidebarContext)
	if (!context) {
		throw new Error('useSidebar must be used within a SidebarProvider.')
	}

	return context
}

const SidebarProvider = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'> & {
		defaultOpen?: boolean
		open?: boolean
		onOpenChange?: (open: boolean) => void
	}
>(
	(
		{
			defaultOpen = true,
			open: openProp,
			onOpenChange: setOpenProp,
			className,
			style,
			children,
			...props
		},
		ref,
	) => {
		// This is the internal state of the sidebar.
		// We use openProp and setOpenProp for control from outside the component.
		const [_open, _setOpen] = React.useState(defaultOpen)
		const open = openProp ?? _open
		const setOpen = React.useCallback(
			(value: boolean | ((value: boolean) => boolean)) => {
				const openState = typeof value === 'function' ? value(open) : value
				if (setOpenProp) {
					setOpenProp(openState)
				} else {
					_setOpen(openState)
				}

				// This sets the cookie to keep the sidebar state.
				document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
			},
			[setOpenProp, open],
		)

		// Helper to toggle the sidebar.
		const toggleSidebar = React.useCallback(() => {
			setOpen((open) => !open)
		}, [setOpen])

		// We add a state so that we can do data-state="expanded" or "collapsed".
		// This makes it easier to style the sidebar with Tailwind classes.
		const state = open ? 'expanded' : 'collapsed'

		const contextValue = React.useMemo<SidebarContext>(
			() => ({
				state,
				open,
				setOpen,
				toggleSidebar,
			}),
			[state, open, setOpen, toggleSidebar],
		)

		return (
			<SidebarContext.Provider value={contextValue}>
				<main
					style={
						{
							'--sidebar-width': SIDEBAR_WIDTH,
							'--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
							...style,
						} as React.CSSProperties
					}
					className={cn(
						'group/sidebar-wrapper has-[[data-variant=inset]]:bg-sidebar flex h-dvh min-h-dvh w-full',
						className,
					)}
					ref={ref}
					{...props}
				>
					{children}
				</main>
			</SidebarContext.Provider>
		)
	},
)
SidebarProvider.displayName = 'SidebarProvider'

const Sidebar = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'> & {
		side?: 'left' | 'right'
		variant?: 'sidebar' | 'floating' | 'inset'
		collapsible?: 'offcanvas' | 'icon' | 'none'
	}
>(
	(
		{
			side = 'left',
			variant = 'inset',
			collapsible = 'icon',
			className,
			children,
			...props
		},
		ref,
	) => {
		const { state } = useSidebar()

		if (collapsible === 'none') {
			return (
				<div
					className={cn('w-(--sidebar-width) flex h-full flex-col', className)}
					ref={ref}
					{...props}
				>
					{children}
				</div>
			)
		}

		return (
			<aside
				ref={ref}
				className="group peer hidden desktop:block"
				data-state={state}
				data-collapsible={state === 'collapsed' ? collapsible : ''}
				data-variant={variant}
				data-side={side}
			>
				<div
					className={cn(
						'relative inset-y-0 z-20 hidden h-full overflow-hidden border-r border-border-grey-50 bg-white transition-[width] duration-300 ease-in-out tablet:flex',
						side === 'left' ? 'left-0' : 'right-0',
						state === 'collapsed'
							? 'w-[var(--sidebar-width-icon)]'
							: 'w-[var(--sidebar-width)]',
						variant === 'floating' || variant === 'inset' ? 'px-4 py-6' : '',

						className,
					)}
					{...props}
				>
					<div
						data-sidebar="sidebar"
						className="flex h-full w-full flex-col gap-10 bg-white"
					>
						{children}
					</div>
				</div>
			</aside>
		)
	},
)
Sidebar.displayName = 'Sidebar'

const SidebarTrigger = React.forwardRef<
	React.ElementRef<typeof Button>,
	React.ComponentProps<typeof Button>
>(({ className, onClick, children, ...props }, ref) => {
	const { toggleSidebar } = useSidebar()

	return (
		<button
			ref={ref}
			data-sidebar="trigger"
			size="icon"
			className={className}
			onClick={(event) => {
				onClick?.(event)
				toggleSidebar()
			}}
			{...props}
		>
			{children}
			<span className="sr-only">Toggle Sidebar</span>
		</button>
	)
})

SidebarTrigger.displayName = 'SidebarTrigger'
export default SidebarTrigger

const SidebarHeader = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-sidebar="header"
			className={cn('flex flex-col gap-2', className)}
			{...props}
		/>
	)
})
SidebarHeader.displayName = 'SidebarHeader'

const SidebarFooter = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-sidebar="footer"
			className={cn('flex flex-col gap-2', className)}
			{...props}
		/>
	)
})
SidebarFooter.displayName = 'SidebarFooter'

const SidebarContent = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-sidebar="content"
			className={cn(
				'flex min-h-0 flex-1 flex-col gap-2 group-data-[collapsible=icon]:overflow-hidden',
				className,
			)}
			{...props}
		/>
	)
})
SidebarContent.displayName = 'SidebarContent'

const SidebarGroup = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-sidebar="group"
			className={cn('relative flex w-full min-w-0 flex-col', className)}
			{...props}
		/>
	)
})
SidebarGroup.displayName = 'SidebarGroup'

const SidebarGroupAction = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
	const Comp = asChild ? Slot : 'button'

	return (
		<Comp
			ref={ref}
			data-sidebar="group-action"
			className={cn(
				'absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center p-0 outline-none transition-transform [&>svg]:size-4 [&>svg]:shrink-0',
				'group-data-[collapsible=icon]:hidden',
				className,
			)}
			{...props}
		/>
	)
})
SidebarGroupAction.displayName = 'SidebarGroupAction'

const SidebarGroupContent = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-sidebar="group-content"
			className={cn('text-sm w-full', className)}
			{...props}
		/>
	)
})
SidebarGroupContent.displayName = 'SidebarGroupContent'

const SidebarMenu = React.forwardRef<
	HTMLUListElement,
	React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => {
	return (
		<ul
			ref={ref}
			data-sidebar="menu"
			className={cn(
				'flex w-full min-w-0 flex-col gap-2 group-data-[collapsible=icon]:gap-6',
				className,
			)}
			{...props}
		/>
	)
})
SidebarMenu.displayName = 'SidebarMenu'

const SidebarMenuItem = React.forwardRef<
	HTMLLIElement,
	React.ComponentProps<'li'>
>(({ className, ...props }, ref) => {
	return (
		<li
			ref={ref}
			data-sidebar="menu-item"
			className={cn('group/menu-item relative', className)}
			{...props}
		/>
	)
})
SidebarMenuItem.displayName = 'SidebarMenuItem'

const sidebarMenuButtonLabelClasses =
	'type-body-medium translate-x-0 scale-100 overflow-hidden whitespace-nowrap opacity-100 transition-all duration-300 ease-in-out group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:-translate-x-2 group-data-[collapsible=icon]:scale-95 group-data-[collapsible=icon]:opacity-0'

const SidebarMenuButton = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<'button'> & {
		asChild?: boolean
		isActive?: boolean
	}
>(({ asChild = false, isActive = false, className, ...props }, ref) => {
	const Comp = asChild ? Slot : 'button'

	const button = (
		<Comp
			ref={ref}
			data-sidebar="menu-button"
			data-active={isActive}
			className={cn(
				'flex items-center justify-start gap-4 rounded-8 p-2 transition-colors duration-300 hover:bg-light-blue',
				isActive ? 'bg-light-blue text-blue' : 'bg-white text-dark-grey',
				className,
			)}
			{...props}
		/>
	)

	return button
})
SidebarMenuButton.displayName = 'SidebarMenuButton'

const SidebarMenuAction = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<'button'> & {
		asChild?: boolean
		showOnHover?: boolean
	}
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
	const Comp = asChild ? Slot : 'button'

	return (
		<Comp
			ref={ref}
			data-sidebar="menu-action"
			className={cn(
				'rounded-tablet absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center p-0 outline-none transition-transform [&>svg]:size-4 [&>svg]:shrink-0',
				showOnHover &&
					'group-focus-within/menu-item:opacity-100 data-[state=open]:opacity-100 group-hover/menu-item:opacity-100 tablet:opacity-0',
				className,
			)}
			{...props}
		/>
	)
})
SidebarMenuAction.displayName = 'SidebarMenuAction'

const SidebarInset = ({ className, ...props }: React.ComponentProps<'div'>) => {
	return (
		<div
			data-slot="sidebar-inset"
			className={cn(
				'relative mx-auto box-content flex h-fit max-w-cabinet-screen-constrained flex-1 flex-col bg-generator-bg px-5 py-6 tablet:px-8 tablet:py-6 desktop:px-6 desktop:py-8',
				className,
			)}
			{...props}
		/>
	)
}

export {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupContent,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	sidebarMenuButtonLabelClasses,
	SidebarMenuItem,
	SidebarProvider,
	SidebarTrigger,
	useSidebar,
}
